// onboarding.ts — the Onboarding screens' own shapes and rules (12 Oct 2026, the makeover).
//
// The operator, 9 Oct 2026: "reads as sections like SoF Passed, KYC passed, Residency/nationality
// passed/flagged/pennding … so that it reads more intuative and easy to spot/assess/check"; 12 Oct: "wallet
// funds should also be listed on that screen itself".
//
// The BFF (bff/src/routes/leads.ts) passes the onboarding worker's four checks (`assessment`) and what each
// person's wallet holds (`walletCheck.holdings`) on every list and record, and a lead's record carries the
// fields its panel reads (the team's words, every chat, the wallet against the claim, Arkham, one-click links).
// The console's shared lib/api.ts still declares the shapes from before these fields; this file declares the
// BFF's fuller ones for this screen alone, so the shared file is untouched until its owner folds them in. The
// requests are api.ts's own — only their answers are read as these shapes here.
//
// THE WORKER DECIDES, THE SCREEN SHOWS: nothing here judges a person. A check the BFF could not read is null
// and shows no verdict; a wallet's holdings that are not known are never "holds none".
//
// 14 Oct 2026 (the operator: "how can I search via mobile number/name/TG Id/email?", "wallet live balance i need to
// see", the bot "asks for pending information until we get everything", "how to find his ID document?"; and, 12 Oct,
// the team asks the customer from the dashboard): every row also carries what the wallet holds now, what the team
// asked and whether the team added their contact; leads and potential leads what the chat waits on them for; the
// record what is still missing, their phone number and a link to each ID image. Two writes are new — the search (a
// POST, so what is typed never sits in a URL) and the team's request — and lib/api.ts has no method for either yet:
// post() below goes through lib/api.ts's one client (request), to go the moment those methods exist there.
import {
  api,
  request,
  type ApprovedClient,
  type ApprovedClients,
  type Lead,
  type LeadClaim,
  type LeadDetails,
  type LeadFacts,
  type LeadQueue,
  type OnboardingLeads,
  type OnboardingProspects,
  type Prospect,
  type MadeParty, type PartyLink, type PartyNameTaken,
} from './api';
import { ApiError } from './errors';
import { dubaiDate, fmtAmount, fmtDate, fmtTime } from './format';
import { foldAsset, sameAsset } from './naming';

// ── the four checks ──────────────────────────────────────────────────────────

export type CheckStatus = 'passed' | 'pending' | 'flagged';
/** One approval check: its verdict and one line of why, in the worker's words (onboarding-agent src/admin/assess.ts). */
export interface Check { status: CheckStatus; reason: string | null }
/** The four checks. A check the BFF could not read is null — no verdict is shown for it. */
export interface Assessment { kyc: Check | null; residency: Check | null; funds: Check | null; wallet: Check | null }
export type CheckKey = keyof Assessment;
/** The four checks in their order: the record's names, and the list's short ones. */
export const CHECKS: ReadonlyArray<{ key: CheckKey; name: string; short: string }> = [
  { key: 'kyc', name: 'KYC', short: 'KYC' },
  { key: 'residency', name: 'Residency & nationality', short: 'Residency' },
  { key: 'funds', name: 'Source of funds', short: 'Funds' },
  { key: 'wallet', name: 'Wallet', short: 'Wallet' },
];
/** The verdict in a word — the word always beside its colour. */
export const VERDICT: Record<CheckStatus, string> = { passed: 'Passed', pending: 'Pending', flagged: 'Flagged' };

// ── the shapes the BFF answers with ──────────────────────────────────────────

/** What a wallet holds of one coin at its check (KYT: stablecoins by contract). */
export interface Holding { asset: string; amount: number }
/** A wallet's check, as every list carries it. */
export type WalletCheck = Lead['walletCheck'] & {
  /** Coin by coin, in the worker's order (USDT, USDC, then the chain's coin); [] holds none of them; null: not known. */
  holdings: Holding[] | null;
};
export type Facts = LeadFacts & {
  /** A USDT or USDC sale whose wallet holds less than the amount: where they said the rest is, in their words. */
  fundsNote: string | null;
};
export type Claim = Omit<LeadClaim, 'amountAed'> & {
  /** The amount in AED — one figure (low = high) or a range; `upTo`: they named only a ceiling ("up to 300k"). */
  amountAed: { low: number; high: number; upTo: boolean } | null;  /** How they pay or want to be paid, as they chose it (27 Oct 2026) — a label only, no rail logic. Absent from a worker
   *  older than it; null: not chosen yet. */
  method?: ClaimMethod | null;
};
/** What the stated wallet holds NOW, read from its chain (onboarding-agent src/onboarding/walletLive.ts): the last good
 *  reading and when; `ok` false — the latest attempt failed, so the reading shown is older (or there is none). Each
 *  field null when the BFF could not read it. */
export interface WalletLive {
  holdings: Holding[] | null;
  readAt: string | null;
  source: string | null;
  ok: boolean | null;
  triedAt: string | null;
  /** A USDT or USDC sale: whether this reading holds the amount claimed. */
  coversClaim: boolean | null;
}
/** One thing still missing, by its key (a code) and its words. */
export interface MissingItem { key: string; label: string }
/** What the chat waits on them for: what (null: not known), their last message, the reminders sent since, until when
 *  the bot can write to them (24 hours after their last message in a @dubaiotc chat; null in the bot's own chat or the
 *  website's), and whether it can now. */
export interface WaitingOn {
  items: MissingItem[] | null;
  lastCustomerAt: string | null;
  reminders: number | null;
  botCanWriteUntil: string | null;
  botCanWrite: boolean | null;
}
/** Their phone number on the record: the number (null: they would rather not say, or it could not be read), whether
 *  Telegram vouches for it (shared with its own button), and how it came. */
export interface Phone { number: string | null; verified: boolean; via: 'telegram_share' | 'typed' | 'team' | null; declined: boolean }

// ── what the team can ask the customer (onboarding-agent src/onboarding/requests.ts) ──

export type RequestItem =
  | 'nationality' | 'residency' | 'in_uae' | 'amount' | 'source_of_funds' | 'purpose' | 'own_money' | 'funds_location'
  | 'wallet' | 'id_image' | 'contact' | 'other';
export type RequestState = 'open' | 'asked' | 'answered' | 'declined' | 'no_answer';
/** A request to the customer: what (its code — null for one this screen has no code for — and its words), its
 *  verification section (null: none — a contact, the team's own question), the team's note, who asked and when, where
 *  it stands, and their words to the team's own question. */
export interface RequestView {
  id: number | null;
  item: RequestItem | null;
  label: string | null;
  check: CheckKey | null;
  note: string | null;
  by: string | null;
  at: string | null;
  state: RequestState | null;
  askedAt: string | null;
  closedAt: string | null;
  answer: string | null;
}
/** Each item's words and its verification section — the worker's own table (ITEMS). */
export const ITEMS: Record<RequestItem, { label: string; check: CheckKey | null }> = {
  nationality: { label: 'Nationality', check: 'residency' },
  residency: { label: 'UAE residence', check: 'residency' },
  in_uae: { label: 'In the UAE now', check: 'residency' },
  amount: { label: 'Amount', check: 'funds' },
  source_of_funds: { label: 'Source of funds', check: 'funds' },
  purpose: { label: 'Purpose', check: 'funds' },
  own_money: { label: 'Own money', check: 'funds' },
  funds_location: { label: 'Where the rest of the funds are', check: 'funds' },
  wallet: { label: 'Wallet', check: 'wallet' },
  id_image: { label: 'ID image', check: 'kyc' },
  contact: { label: 'Contact', check: null },
  other: { label: 'Something else', check: null },
};
/** What each check's "Ask the customer" offers: its own items, in the worker's order. */
export const ASKABLE: Record<CheckKey, RequestItem[]> = Object.fromEntries(
  CHECKS.map((k) => [k.key, (Object.keys(ITEMS) as RequestItem[]).filter((i) => ITEMS[i].check === k.key)]),
) as Record<CheckKey, RequestItem[]>;
/** A request's words: the worker's, else the item's own. */
export const requestLabel = (r: Pick<RequestView, 'item' | 'label'>): string => r.label ?? (r.item ? ITEMS[r.item].label : 'A request');
/** Where a request stands, in words. */
export const REQUEST_STATE_WORD: Record<RequestState, string> = {
  open: 'Not sent yet', asked: 'Asked', answered: 'Answered', declined: 'Would rather not say', no_answer: 'No answer after 7 days',
};
/** Still waiting on the customer: not sent yet, or sent and not answered. */
export const isLive = (r: RequestView) => r.state === 'open' || r.state === 'asked';

/** The BFF's answer to a request: the items asked now, those already waiting, and when it goes out. */
export interface Asked {
  created: RequestItem[] | null;
  alreadyOpen: RequestItem[] | null;
  delivery: 'now' | 'waiting' | 'held' | null;
  via: 'bot_dm' | 'business' | 'web' | null;
}
/** What the line under the form says once a request is made (the brief's words): when the bot asks, and what was
 *  already waiting — or, with nothing new, that it was already asked. */
export function askedWords(a: Asked): { text: string; tone: 'plain' | 'warn' } {
  const names = (xs: RequestItem[]) => xs.map((x) => ITEMS[x].label).join(', ');
  const created = a.created ?? [];
  const already = a.alreadyOpen ?? [];
  if (a.created !== null && created.length === 0 && already.length > 0) return { text: `Already asked, still waiting: ${names(already)}`, tone: 'plain' };
  const when = a.delivery === 'now' ? 'The bot is asking now'
    : a.delivery === 'waiting' ? 'Goes out with their next message'
      : a.delivery === 'held' ? 'Held — the team is in the chat'
        : 'Sent to the bot — when it goes out was not said';
  const asked = created.length ? ` — ${names(created)}` : '';
  const waiting = already.length ? ` · already waiting: ${names(already)}` : '';
  return { text: `${when}${asked}${waiting}`, tone: a.delivery === 'held' || a.delivery === null ? 'warn' : 'plain' };
}

// ── the search (POST /api/onboarding/search) ──────────────────────────────────

export type FoundBy = 'name' | 'username' | 'telegram_id' | 'ref' | 'phone' | 'chat' | 'answers' | 'profile';
/** What a person was found by, in words (the brief's). */
export const FOUND_WORD: Record<FoundBy, string> = {
  telegram_id: 'Telegram id', ref: 'card ref', phone: 'phone number', username: '@username', name: 'name',
  chat: 'in their chat', answers: 'in their answers', profile: 'in an old record',
};
export type ReadAs = 'name' | 'telegram_id' | 'ref' | 'phone' | 'email';
/** A person found who is on none of the lists, and where they stand. */
export interface ElsewherePerson {
  id: string | null;
  ref: string | null;
  customerId: string | null;
  who: string | null;
  name: string | null;
  channel: Lead['channel'];
  standing: 'rejected' | 'refused' | 'blocked' | 'chatting' | 'other' | null;
  lastActiveAt: string | null;
}
export interface SearchResult {
  at: string | null;
  readAs: ReadAs[];
  total: number | null;
  more: boolean | null;
  needsYou: { found: FoundBy[]; lead: LeadRow }[];
  potential: { found: FoundBy[]; prospect: ProspectRow }[];
  clients: { found: FoundBy[]; client: ClientRow }[];
  elsewhere: { found: FoundBy[]; person: ElsewherePerson }[];
  /** Rows the worker sent that the Books could not show: said — never "nobody found" for them. */
  unreadable: number;
  /** The BFF's clock when it asked the worker — every age in the results is counted to it. */
  fetched_at: string;
}

// ── the seven-section checklist (27 Oct 2026; onboarding-bot docs/onboarding/CHECKLIST-SPEC.md) ──
//
// The operator, 27 Oct 2026: "Break customer onboarding checklist into multiple scenarios, like ID verification, wallet
// check, their requirement related info, physical availability etc so they are easily readable for operators"; "Wallet
// screening and matching against their claim is still most important". The worker writes every word; the screen keeps
// the order (wallet · id · requirement · presence · funds · mismatch · history) and the words, which are the Telegram
// card's. A check that never ran is `pending`, never `checked`. `tier` is null until the risk-tier design is approved.

export type ChecklistKey = 'wallet' | 'id' | 'requirement' | 'presence' | 'funds' | 'mismatch' | 'history';
/** checked — ran, found nothing against them · look — for the operator's eye · failed — what the rules refuse or a grave
 *  mismatch (the operator still decides) · pending — not asked, not received, or never ran. */
export type ChecklistStatus = 'checked' | 'look' | 'failed' | 'pending';
export interface ChecklistSection {
  key: ChecklistKey;
  title: string;
  status: ChecklistStatus;
  /** ONE line in view: the fact first. */
  line: string;
  /** Behind a tap: one fact a line. */
  detail: string[];
}
export interface Checklist { sections: ChecklistSection[]; tier: null }
/** The customer's stated way to pay or be paid (claim.method). */
export type ClaimMethod = 'manager_cheque' | 'bank_transfer' | 'cash' | 'crypto';
/** Every row and record from a worker that writes the checklist; absent (or null) from an older one, and on approved
 *  clients until the operator says otherwise. */
interface Since27Oct { checklist?: Checklist | null }

/** THE WALLET ON A LIST ROW (his "wallet check for all the customers … balance … tronscan link on the main dashboard", 1 Nov
 *  2026; the field agreed with KYT): the address, its network in TERMINOLOGY's spelling, and its explorer page (https, the
 *  hosts the BFF allows; null: none). null: no wallet given yet; absent from a worker older than it. */
export interface RowWallet { address: string; network: string; explorer: string | null }
interface Since1Nov { wallet?: RowWallet | null }

/** The explorer by its own name, from its page: Tronscan, Etherscan, BscScan, mempool; else "Explorer". */
export function explorerName(url: string): string {
  const host = (/^https:\/\/([^/]+)/.exec(url)?.[1] ?? '').replace(/^www\./, '');
  return ({ 'tronscan.org': 'Tronscan', 'etherscan.io': 'Etherscan', 'bscscan.com': 'BscScan', 'mempool.space': 'mempool', 'basescan.org': 'BaseScan',
    'blockstream.info': 'Blockstream', 'polygonscan.com': 'PolygonScan', 'arbiscan.io': 'Arbiscan' } as Record<string, string>)[host] ?? 'Explorer';
}

/** The network as TERMINOLOGY §2 spells it: TRC20 · ERC20 · BEP20 · BTC as sent; the worker's upper-cased BASE, POLYGON and
 *  ARBITRUM (normalizeChainTag) as names — Base, Polygon, Arbitrum; any other tag as sent. */
export function networkWord(network: string): string {
  return ({ BASE: 'Base', POLYGON: 'Polygon', ARBITRUM: 'Arbitrum', SOL: 'SOL' } as Record<string, string>)[network] ?? network;
}

/** The fields every row carries since 14 Oct, null from a worker older than them. */
interface Since14Oct {
  /** The team added their contact — said so, never "not verified". */
  contactByTeam: boolean;
  walletLive: WalletLive | null;
  requests: RequestView[] | null;
}

export type LeadRow = Omit<Lead, 'walletCheck' | 'facts' | 'claim'> & Since14Oct & Since27Oct & Since1Nov & {
  claim: Claim; facts: Facts; walletCheck: WalletCheck; assessment: Assessment | null; waitingOn: WaitingOn | null;
  /** Ready for the team's decision — the worker's word, and its order puts these first; false from a worker older than it. */
  ready: boolean;
};
export type ProspectRow = Omit<Prospect, 'facts' | 'claim'> & Since14Oct & Since27Oct & Since1Nov & {
  claim: Claim & { asset: Prospect['claim']['asset'] }; facts: Facts;
  /** The bot's own check of the stated wallet; null from a worker older than it. */
  walletCheck: WalletCheck | null; assessment: Assessment | null; waitingOn: WaitingOn | null;
};
export type ClientRow = Omit<ApprovedClient, 'facts' | 'claim'> & Since14Oct & Since27Oct & Since1Nov & {
  claim: Claim | null; facts: Facts | null;
  /** The approving chat's card's check; null from a worker older than it. */
  walletCheck: WalletCheck | null; assessment: Assessment | null;
};
export type LeadsList = Omit<OnboardingLeads, 'leads'> & { leads: LeadRow[] };
export type ProspectsList = Omit<OnboardingProspects, 'prospects'> & { prospects: ProspectRow[] };
export type ClientsList = Omit<ApprovedClients, 'clients'> & { clients: ClientRow[] };

/** Arkham's stored answer for the address (bought only when a card goes; the record never buys one). */
export interface ArkhamAnswer {
  status: 'clean' | 'flagged' | 'unscreened' | 'error' | 'not_enabled' | 'budget_exhausted' | null;
  entity: string | null;
  labels: string[];
  band: 'low' | 'medium' | 'high' | 'severe' | null;
  score: number | null;
  checkedAt: string | null;
}
export type TimelineKind = 'card' | 'card_failed' | 'escalated' | 'approved' | 'rejected' | 'contacted' | 'handled' | 'refused' | 'declined' | 'note';
export type ChatApp = 'telegram' | 'web' | 'whatsapp';
export type PersonChannel = 'business' | 'bot-dm' | 'web' | 'whatsapp';
/** One lead's whole record, as its panel reads it. */
export type LeadRecord = Omit<LeadDetails, 'answers' | 'wallet' | 'chat' | 'timeline'> & Since14Oct & Since27Oct & {
  waitingOn: WaitingOn | null;
  /** What is still missing, whoever wrote last, in the order the bot asks; null from a worker older than it. */
  stillMissing: MissingItem[] | null;
  phone: Phone | null;
  /** Each ID image's message in the ops group, oldest first; null: none sent, or not known. */
  idImageLinks: string[] | null;
  answers: LeadDetails['answers'] & { type: LeadClaim['type']; amountAed: Claim['amountAed']; fundsNote: string | null; method?: ClaimMethod | null };
  wallet: (NonNullable<LeadDetails['wallet']> & {
    balanceUsdt: number | null;
    coversClaim: boolean | null;
    /** The amount they named, in USDT (the worker's rate). */
    claimUsdt: { low: number; high: number } | null;
    holdings: Holding[] | null;
    arkham: ArkhamAnswer | null;
    /** One click: the address on its chain's explorer, and on Arkham — https, the hosts the BFF allows, or null. */
    links: { transactions: string | null; arkham: string | null };
  }) | null;
  assessment: Assessment | null;
  /** The newest 40 messages across every chat of the person, oldest first; `channel` null: the record's own chat. */
  chat: { from: 'customer' | 'bot' | 'team'; channel: ChatApp | null; at: string | null; text: string }[];
  /** Every chat of the person, this one first. */
  channels: PersonChannel[];
  timeline: { at: string | null; kind: TimelineKind | null; what: string; by: string | null }[];
};

/** A POST to the BFF through lib/api.ts's one client — the same sign-in, the same refusal (one ApiError, in words), the same return to the login screen
 *  when the web session has died, and the same bound. `reads`: a POST that only reads (the search). */
function post<T>(path: string, body: unknown, reads = false): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body), ...(reads ? { reads: true } : {}) });
}

/** The screen's reads: api.ts's requests, their answers read as the BFF's fuller shapes — and its two POSTs. */
export const onboardingApi = {
  leads: (page?: { queue: LeadQueue; offset: number }) => api.onboardingLeads(page) as unknown as Promise<LeadsList>,
  lead: (id: string) => api.onboardingLead(id) as unknown as Promise<LeadRecord>,
  clients: (offset = 0) => api.onboardingClients(offset) as unknown as Promise<ClientsList>,
  prospects: (offset = 0) => api.onboardingProspects(offset) as unknown as Promise<ProspectsList>,
  /** Find a person on every tab at once. In the body, never the URL: what is typed is a phone number or a name. */
  search: (q: string) => post<SearchResult>('/api/onboarding/search', { q }, true),
  /** Ask the customer for these items — who asks is whoever is signed in, which the BFF says, never the browser. */
  ask: (sessionId: string, items: RequestItem[], note: string | null) =>
    post<Asked>('/api/onboarding/requests', { sessionId, items, ...(note ? { note } : {}) }),
  /** "Make a party" (D3, 15 Oct 2026): from the record's name; `link` an existing party instead; `slug` a chosen one. */
  makeParty: (sessionId: string, how: { link?: string; slug?: string } = {}) =>
    post<MadeParty>('/api/onboarding/party', { sessionId, ...how }),
};

/** The line under "Make a party", in words: what happened, or why not. A 409 `name_taken` is handled by the button
 *  itself (it offers the existing party); every other failure is said as the console says failures. */
export type { PartyLink };
export function madePartyWords(r: MadeParty): { text: string; tone: 'plain' } {
  return { text: r.made ? `Party made: ${r.party.name}` : `Linked to the party ${r.party.name}`, tone: 'plain' };
}
/** A refusal of "Make a party" as the BFF sends it, when it is one the button acts on. */
export function partyNameTaken(e: unknown): PartyNameTaken | null {
  if (!(e instanceof ApiError) || e.status !== 409 || !e.body || typeof e.body !== 'object') return null;
  const b = e.body as Record<string, unknown>;
  const ex = b.existing as Record<string, unknown> | undefined;
  return b.error === 'name_taken' && ex && typeof ex.slug === 'string' && typeof ex.name === 'string'
    ? { error: 'name_taken', reason: typeof b.reason === 'string' ? b.reason : '', existing: { slug: ex.slug, name: ex.name, entity: ex.entity === 'indiaotc' ? 'indiaotc' : 'dubaiotc' } }
    : null;
}

/** An age in words. How old a read is by the viewer's clock — the one thing on this screen that is (a phone a few
 *  seconds behind the Worker reads "just now", never a negative age) — or an age counted to the read. */
export function ago(ms: number): string {
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)} min ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)} h ago`;
  return `${Math.floor(ms / 86_400_000)} d ago`;
}

// ── what the wallet holds, in words ──────────────────────────────────────────

/** The worker's own words when the wallet check is pending for want of a wallet (onboarding-agent src/admin/assess.ts
 *  wallet(): `if (!w.given) … 'No wallet given yet'`). The lists carry no wallet address, so this is how a row knows
 *  none was given — and a potential lead's missing answers say it too. */
export const NO_WALLET_REASON = 'No wallet given yet';
export const noWalletYet = (a: Assessment | null, missing?: string[] | null): boolean =>
  (a?.wallet?.status === 'pending' && a.wallet.reason === NO_WALLET_REASON) || !!missing?.includes('wallet_address');

/** One coin in a wallet line, and its colour: the coin on sale wears the funds result — green when the wallet covers
 *  the claim, red when it is short; every other coin is plain. */
export interface HeldPart { asset: string; amount: number; tone: 'pos' | 'neg' | null }
/** When a live reading was taken, after its coins — "3 min ago" — or that the chain did not answer the latest attempt
 *  and when the reading shown was taken, in amber. */
export interface LineWhen { text: string; tone: 'muted' | 'warn' }
/** A wallet line: what it holds coin by coin, or why there is nothing to list, in words — and, read live, when. */
export type WalletLine =
  | { kind: 'held'; parts: HeldPart[]; when?: LineWhen }
  | { kind: 'words'; text: string; tone: 'pos' | 'neg' | 'muted' | null; when?: LineWhen };

/** The coin a stablecoin sale is checked against (USDT or USDC), or null — a buy, or a sale of another coin. */
export function saleCoin(c: { side: 'buy' | 'sell' | null; asset: string | null } | null): 'USDT' | 'USDC' | null {
  if (c?.side !== 'sell') return null;
  const coin = foldAsset(c.asset);
  return coin === 'USDT' || coin === 'USDC' ? coin : null;
}

/** The network's own coin, for "holds none of USDT, USDC or TRX" — null when the network is not one these know. */
export function chainCoin(chain: string | null): string | null {
  const c = (chain ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (c.startsWith('TRC') || c === 'TRON' || c === 'TRX') return 'TRX';
  if (c.startsWith('ERC') || c === 'ETH' || c === 'ETHEREUM' || c.startsWith('ARB') || c === 'BASE') return 'ETH';
  if (c.startsWith('BEP') || c === 'BSC' || c === 'BNB') return 'BNB';
  if (c.startsWith('POLY') || c === 'MATIC' || c === 'POL') return 'POL';
  if (c === 'BTC' || c === 'BITCOIN') return 'BTC';
  return null;
}

/**
 * What the wallet holds, as the wallet line says it (the coordinator's brief from the operator, 12 Oct 2026):
 *   1. its holdings, when they are a list — coin by coin, in the worker's order, the coin on sale first in the colour
 *      of the funds result; the coin on sale missing from the list is none of it (KYT's rule), so it stands first as
 *      "0 USDC", in red (12 Oct 2026); an empty list, on a buy, holds none of them;
 *   2. else its USDT balance, when an older check gave one: "~30,000 USDT";
 *   3. else "No wallet yet", when the worker's wallet check is pending for want of a wallet;
 *   4. else "Balance not read" — never a 0 for what is not read (his "wallet check for all the customers", 1 Nov 2026).
 * `chain`: the record's network, for naming its coin when the wallet holds none; a list row has none and says "Empty".
 */
export function walletLine(
  w: Pick<WalletCheck, 'holdings' | 'coversClaim'> & { balanceUsdt?: number | null } | null, sale: 'USDT' | 'USDC' | null, noWallet: boolean, chain?: string | null,
): WalletLine {
  const tone = (coin: string): 'pos' | 'neg' | null =>
    (!sameAsset(coin, sale) ? null : w?.coversClaim === true ? 'pos' : w?.coversClaim === false ? 'neg' : null);
  if (w?.holdings) {
    // The coin on sale missing from a list that is there is none of it (KYT's rule): "0 USDC", first, in red — the
    // wallet cannot cover a sale it holds none of. Never for a list that is not known (null): no 0 for an unknown.
    const none = sale !== null && !w.holdings.some((h) => sameAsset(h.asset, sale));
    if (none) return { kind: 'held', parts: [{ asset: sale, amount: 0, tone: 'neg' }, ...w.holdings.map((h) => ({ ...h, tone: null }))] };
    if (w.holdings.length === 0) {
      if (chain === undefined) return { kind: 'words', text: 'Empty', tone: sale && w.coversClaim === false ? 'neg' : 'muted' };
      const coin = chainCoin(chain);
      const text = coin === 'BTC' ? 'Holds no BTC' : `Holds none of USDT, USDC or ${coin ?? "the network's coin"}`;
      return { kind: 'words', text, tone: sale && w.coversClaim === false ? 'neg' : 'muted' };
    }
    const first = w.holdings.filter((h) => sameAsset(h.asset, sale));
    const rest = w.holdings.filter((h) => !sameAsset(h.asset, sale));
    return { kind: 'held', parts: [...first, ...rest].map((h) => ({ ...h, tone: tone(h.asset) })) };
  }
  // A field a Books older than it never sends reads as not known, the same as null.
  if (w && typeof w.balanceUsdt === 'number') {
    return { kind: 'words', text: `~${fmtCoin(w.balanceUsdt, 'USDT')} USDT`, tone: sale === 'USDT' ? (tone('USDT') ?? null) : null };
  }
  if (noWallet) return { kind: 'words', text: 'No wallet yet', tone: 'muted' };
  return { kind: 'words', text: 'Balance not read', tone: 'muted' };
}

/** A live reading that can be listed: taken at a known time, with a list of what it holds. */
export const liveReading = (l: WalletLive | null | undefined): l is WalletLive & { readAt: string; holdings: Holding[] } =>
  !!l && l.readAt !== null && Array.isArray(l.holdings);

/**
 * The wallet line with what the wallet holds NOW (the operator, 14 Oct 2026: "wallet live balance i need to see"):
 * a live reading taken at a known time is listed by walletLine's own rules — the coin on sale first, green when this
 * reading covers the claim, red when short, "0 USDC" for a sale's coin missing from it — then when it was taken,
 * counted to the read (`readMs`, the BFF's clock): "3 min ago". A latest attempt that failed keeps the older reading
 * and says so, in amber: "the chain didn't answer · last read 2 h ago". Never read — or nothing it could list — the
 * line the check gave (walletLine), as before.
 */
export function liveLine(
  live: WalletLive | null | undefined, check: WalletCheck | null, sale: 'USDT' | 'USDC' | null, noWallet: boolean, readMs: number, chain?: string | null,
): WalletLine {
  if (!liveReading(live)) return walletLine(check, sale, noWallet, chain);
  const t = Date.parse(live.readAt);
  const age = Number.isFinite(t) && Number.isFinite(readMs) ? ago(Math.max(0, readMs - t)) : 'at a time not known';
  const line = walletLine({ holdings: live.holdings, coversClaim: live.coversClaim }, sale, false, chain);
  return { ...line, when: live.ok === false ? { text: `the chain didn't answer · last read ${age}`, tone: 'warn' } : { text: age, tone: 'muted' } };
}

/** A coin amount in the books' format (lib/format: international grouping, up to four places — five for BTC). */
export const fmtCoin = (amount: number, asset: string) => fmtAmount(amount, asset);

// ── their phone number ───────────────────────────────────────────────────────

/** A number as people read it: a UAE mobile "+971 50 999 8877", a UAE landline "+971 4 123 4567"; any other country's
 *  as given (+ and its digits) — no grouping guessed for a plan this screen does not know. */
export function fmtPhone(e164: string): string {
  const m = /^\+971(5\d)(\d{3})(\d{4})$/.exec(e164) ?? /^\+971([2-9])(\d{3})(\d{4})$/.exec(e164);
  return m ? `+971 ${m[1]} ${m[2]} ${m[3]}` : e164;
}
/** How a number came, in the brief's words: verified by Telegram (shared with its own button), typed by them, added
 *  by the team; null when not known. */
export function phoneHow(p: Phone): string | null {
  if (p.verified) return 'verified by Telegram';
  if (p.via === 'typed') return 'typed by them';
  if (p.via === 'team') return 'added by the team';
  if (p.via === 'telegram_share') return 'shared in Telegram';
  return null;
}

// ── what the chat waits on them for ──────────────────────────────────────────

/**
 * "Waiting on them" in one line (the operator, 14 Oct 2026: the bot "asks for pending information until we get
 * everything"; the brief's words): what is missing, the reminders sent since their last message, and until when the
 * bot can write to them — on the Dubai clock, 24 h, with the day when it is not the read's. When the bot cannot write
 * any more the line says so, in amber, and that it is the team's to do: Telegram lets a business account's bot write
 * only within 24 hours of the customer's last message; the website chat only while they are on the page.
 */
export function waitingWords(w: WaitingOn, channel: Lead['channel'], readMs: number, items = true): { text: string; tone: 'plain' | 'warn' } {
  const what = w.items && w.items.length > 0 ? w.items.map((x) => x.label).join(' · ') : 'what for was not said';
  const reminded = w.reminders ? `${w.reminders} reminder${w.reminders === 1 ? '' : 's'} sent` : null;
  // `items` false: the record's "Still missing" chips already name them — the line keeps what only it knows.
  const head = [items ? `Waiting on them: ${what}` : 'Waiting on them', reminded].filter(Boolean).join(' · ');
  if (w.botCanWrite === false) {
    const why = channel === 'web'
      ? 'the bot can ask them only when they are back in the website chat'
      : w.botCanWriteUntil !== null || channel === 'business'
        ? "the bot can't write to them any more (Telegram's 24-hour limit): message them yourself"
        : "the bot can't write to them now: message them yourself";
    return { text: `${head} — ${why}`, tone: 'warn' };
  }
  if (w.botCanWrite === true) {
    // No deadline: their own chat with the bot, which it may write in any time (the contract's null). Anywhere else a
    // deadline not read is not "any time": the bot can write now, until when not known.
    if (!w.botCanWriteUntil) return { text: `${head} · the bot can write to them ${channel === 'bot-dm' ? 'any time' : 'now'}`, tone: 'plain' };
    const sameDay = Number.isFinite(readMs) && dubaiDate(w.botCanWriteUntil) === dubaiDate(readMs);
    return { text: `${head} · the bot can write until ${sameDay ? fmtTime(w.botCanWriteUntil) : fmtDate(w.botCanWriteUntil)}`, tone: 'plain' };
  }
  return { text: head, tone: 'plain' };
}

// ── the claim against the wallet ─────────────────────────────────────────────

/** How much of the claim the wallet holds of the coin on sale, and how short it is — from the worker's own figures
 *  (what it holds, the claim in USDT at its rate); null when either is not known. The percentage is floored, as the
 *  worker's own reason floors it, so the two never disagree. */
export function shortfall(held: number | null, claimLow: number | null): { pct: number; short: number } | null {
  if (held === null || claimLow === null || !(claimLow > 0)) return null;
  // As the worker writes it (admin/assess.ts): multiplied first, then a hair over — 58,000 of 100,000 is 58%, never 57.
  return { pct: Math.floor((held * 100) / claimLow + 1e-9), short: Math.max(0, claimLow - held) };
}
/** What the wallet holds of `coin`: its list's figure — a coin missing from a list that is there is none of it (the
 *  worker's rule) — or, without a list, its USDT balance for USDT; else not known. */
export function heldOf(w: { holdings?: Holding[] | null; balanceUsdt?: number | null } | null, coin: 'USDT' | 'USDC'): number | null {
  if (!w) return null;
  if (w.holdings) return w.holdings.find((h) => sameAsset(h.asset, coin))?.amount ?? 0;
  return coin === 'USDT' && typeof w.balanceUsdt === 'number' ? w.balanceUsdt : null;
}
